import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { gallery } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-blue-800 shadow-lg transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:h-12 md:w-12';
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const current = gallery[index];
  const goTo = (next: number) => setIndex((next + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  return <section className="w-full bg-teal-900 py-14 text-white md:py-20" data-id="element-61" data-mp="rcb07nn17r6n">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-62" data-mp="wamafzh8o3pe">
        <header className="mx-auto max-w-4xl text-center" data-id="element-63" data-mp="112meo44cdirs">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-blue-200" data-id="element-64" data-mp="1135xdn1tgb6v">
            Mira el Atlas por dentro
          </p>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight md:text-5xl" data-id="element-65" data-mp="8dyphxgh298y">
            Así se estudia cada solución
          </h2>
        </header>

        <div role="group" aria-roledescription="carrusel" aria-label="Páginas de muestra del Atlas" tabIndex={0} onKeyDown={handleKeyDown} className="relative mx-auto mt-12 max-w-xl rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400" data-id="element-66" data-mp="dew7a8xkl19x">
          <div className="overflow-hidden rounded-2xl" data-id="element-67" data-mp="1xs3q6zq09z1e">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-68" data-mp="1xupzb3sjomlq">
              {gallery.map((item, itemIndex) => <figure key={item.image} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-69" data-mp="366p9bzu2g2jc" data-mpi={itemIndex}>
                  <img src={item.image} alt={`Página ${itemIndex + 1} del Atlas Visual de Terapia Intravenosa`} className="pointer-events-none w-full rounded-2xl bg-white" draggable={false} loading={itemIndex === 0 ? 'eager' : 'lazy'} data-id="element-70" data-mp="3kvd2jzgfw064" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Página anterior" className={`${arrowClass} left-2 md:-left-5`} data-id="element-71" data-mp="12w3rkpky90pc">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-72" data-mp="tyjp5dbcoyd5" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Página siguiente" className={`${arrowClass} right-2 md:-right-5`} data-id="element-73" data-mp="xzdco5kvsb5i">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-74" data-mp="1qmprftksveo9" />
          </button>
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center" aria-live="polite" data-id="element-75" data-mp="1cmyfdydug8cw">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-76" data-mp="1hli24109ho6b">{current.title}</p>
          <p className="mt-2 text-blue-100" data-id="element-77" data-mp="179tzbg8nhe7">{current.caption}</p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-78" data-mp="67xjttsqds6u">
          {gallery.map((item, itemIndex) => <button key={item.image} type="button" onClick={() => goTo(itemIndex)} aria-label={`Ver página ${itemIndex + 1} de ${total}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 ${itemIndex === index ? 'w-8 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} data-id="element-79" data-mp="li2w7xoxtjro" data-mpi={itemIndex} />)}
        </div>

        <p className="mt-6 text-center text-sm text-blue-200" data-id="element-80" data-mp="ve60b93cnsh9">
          Desliza o utiliza las flechas — páginas reales del Atlas
        </p>

        <div className="mt-8 flex flex-col items-center text-center" data-id="element-81" data-mp="qiguwp4nfhd1">
          <CtaButton variant="secondary" size="md" data-id="element-82" data-mp="3g712avt4g631">
            Quiero acceder al Sistema Completo
          </CtaButton>
          <p className="mt-3 text-sm text-blue-100" data-id="element-83" data-mp="3b8g1ov9yjke2">
            Acceso inmediato · PDF imprimible · 6 Tomos + 5 Bonos
          </p>
        </div>
      </div>
    </section>;
}